'use client'

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Users, Target, Lightbulb, CheckCircle, Quotes, MagnifyingGlass, ChatCircle, Lightning, TrendUp } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

export default function PromoAI() {

  return (
    <div className="min-h-screen relative overflow-hidden transition-none">
      
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">


          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-[var(--brutal-ink)] mb-6 leading-tight">
              PromoAI Enterprise Planning App
            </h1>
            <p className="text-xl text-[var(--brutal-muted)] mb-8 leading-relaxed max-w-4xl mx-auto">
              How I built a research-driven UX culture from scratch and transformed our product roadmap through systematic user research and strategic prioritization.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">User Research</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Design Thinking</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Strategic Planning</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">AI Integration</Badge>
            </div>
          </div>

          {/* The Challenge */}
          <div className="mb-12">
            {/* Subtle divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
            
            <Card className="brutal-panel p-4 md:p-8 mb-12 md:block hidden">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">The Challenge</h2>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed max-w-4xl mx-auto mb-8">
                  Building a comprehensive Promo planning SaaS solution from previous consulting implementations, we discovered the product had been developed on assumptions rather than real user research insights.
                </p>
              </div>
            </Card>
            
            {/* Mobile version without card */}
            <div className="md:hidden text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">The Challenge</h2>
              <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
                Building a comprehensive Promo planning SaaS solution from previous consulting implementations, we discovered the product had been developed on assumptions rather than real user research insights.
              </p>
            </div>
          </div>

          {/* Hero Image Carousel */}
          <div className="mb-16">
            <Card variant="media" className="brutal-panel overflow-hidden !gap-0 !py-0">
              <Components.ImageCarousel
                images={[
                  {
                    src: "/assets/problem-solving.png",
                    alt: "PromoAI Challenge Overview showing design workshop identifying problem solving strategies",
                    title: "Challenge Overview",
                    description: "User journey gaps identified through research"
                  },
                  {
                    src: "/assets/user-test-plan.png",
                    alt: "PromoAI user research setup showing interview and testing methodology",
                    title: "Research Methodology",
                    description: "Comprehensive user research and testing approach"
                  },
                  {
                    src: "/assets/user-test-findings-example.png",
                    alt: "PromoAI current interface showing areas of complexity and user confusion",
                    title: "Current Experience Issues",
                    description: "Existing challenges in the user interfacethat needed to be addressed"
                  }
                ]}
              />
              <div className="p-4 md:p-6 text-center">
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4">PromoAI Challenge Overview</h3>
                <p className="text-[var(--brutal-muted)] mb-4">Research-driven approach to understanding user pain points and interface complexity</p>
                <div className="grid grid-cols-2 gap-4 text-left text-[var(--brutal-muted)] text-sm max-w-md mx-auto">
                  <div>• User research methodology</div>
                  <div>• Interface complexity analysis</div>
                  <div>• Pain point identification</div>
                  <div>• Strategic planning approach</div>
                </div>
              </div>
            </Card>
          </div>

          <Components.KeyDecisions
            decisions={[
              {
                question: 'Start over, or document and improve what we had?',
                tension:
                  'A clean-slate redesign would have looked decisive. Teams already worked inside an existing design language, and throwing it out would have burned months of shared context.',
                choice:
                  'We documented and improved the existing design into a reusable design system the product could keep building on.',
              },
              {
                question: 'Trust inherited assumptions, or test the journey with users?',
                tension:
                  'The product already had screens from earlier work. Shipping on those assumptions was faster. It left open which parts of the design actually matched how planners worked.',
                choice:
                  'We ran usability tests and interviews to map the user journey and see which design assumptions held up.',
              },
              {
                question: 'Chase loud tickets, or prioritize with a shared method?',
                tension:
                  'Fixing whatever screamed loudest kept the queue moving. It did not tell us how usability debt stacked against roadmap work and bugs.',
                choice:
                  'We set up a usability-issue process, compared prioritization methods against our case, and chose RICE to shape the roadmap.',
              },
            ]}
          />

          {/* The Process */}
          <div className="mb-12">
            {/* Subtle divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
            
            <Card className="brutal-panel p-4 md:p-8 mb-12 md:block hidden">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">The Process</h2>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed max-w-4xl mx-auto">
                  We implemented a structured, but not rigid, process that introduced design thinking to the team. This helped us to create a shared ownership of ideas and drive improvement across the app.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                {[
                  { n: '1', title: 'Plan', body: 'Defined research objectives and stakeholder alignment', chip: 'bg-[var(--brutal-mustard)]' },
                  { n: '2', title: 'Empathize', body: 'Conducted user interviews and gathered insights', chip: 'bg-[#7eb8da]' },
                  { n: '3', title: 'Define', body: 'Synthesized findings into clear problem statements', chip: 'bg-[#8fce8f]' },
                  { n: '4', title: 'Ideate', body: 'Generated solutions through collaborative workshops', chip: 'bg-[var(--brutal-accent)]' },
                  { n: '5', title: 'Test', body: 'Validated solutions with users and stakeholders', chip: 'bg-[#c4a7e7]' },
                ].map(({ n, title, body, chip }) => (
                  <div key={n} className="brutal-panel p-6 text-center">
                    <div className={`w-12 h-12 aspect-square ${chip} border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-[10px] flex items-center justify-center text-[var(--brutal-ink)] mx-auto mb-4 flex-shrink-0`}>
                      <span className="font-bold text-sm">{n}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2 break-words">{title}</h3>
                    <p className="text-[var(--brutal-ink)] text-sm break-words">{body}</p>
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Mobile version without card */}
            <div className="md:hidden">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">The Process</h2>
                <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
                  We implemented a structured, but not rigid, process that introduced design thinking to the team. This helped us to create a shared ownership of ideas and drive improvement across the app.
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-3">
                {[
                  { n: '1', title: 'Plan', body: 'Defined research objectives and stakeholder alignment', chip: 'bg-[var(--brutal-mustard)]' },
                  { n: '2', title: 'Empathize', body: 'Conducted user interviews and gathered insights', chip: 'bg-[#7eb8da]' },
                  { n: '3', title: 'Define', body: 'Synthesized findings into clear problem statements', chip: 'bg-[#8fce8f]' },
                  { n: '4', title: 'Ideate', body: 'Generated solutions through collaborative workshops', chip: 'bg-[var(--brutal-accent)]' },
                  { n: '5', title: 'Test', body: 'Validated solutions with users and stakeholders', chip: 'bg-[#c4a7e7]' },
                ].map(({ n, title, body, chip }) => (
                  <div key={n} className="brutal-panel p-4 flex flex-row items-start gap-4 text-left">
                    <div className={`w-11 h-11 aspect-square ${chip} border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-[10px] flex items-center justify-center text-[var(--brutal-ink)] shrink-0`}>
                      <span className="font-bold text-sm">{n}</span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-[var(--brutal-ink)] mb-1">{title}</h3>
                      <p className="text-[var(--brutal-muted)] text-sm leading-relaxed">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* What We Did */}
          <div className="mb-12">
            {/* Subtle divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
            
            <Card className="brutal-panel p-4 md:p-8 mb-12 md:block hidden">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">What We Did</h2>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed max-w-4xl mx-auto mb-8">
                  We developed a comprehensive research initiative that maximized impact while maintaining cost efficiency. Our strategic approach included:
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="/assets/advisor-research.png" 
                    alt="User Interviews Process"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">User Interviews</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Conducted extensive interviews to understand real user needs and pain points</p>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="/assets/ux-interviews-ra.png" 
                    alt="Usability Testing Sessions"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Usability Testing</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Gathered insights on how users actually interact with our product</p>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
                    alt="Stakeholder Workshop Sessions"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Stakeholder Workshops</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Hosted workshops to align on findings and define next steps</p>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="/assets/ai-agent-ideation.png" 
                    alt="Design Thinking Methodology"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Design Thinking</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Applied structured methodology to organize and prioritize insights</p>
                  </div>
                </div>
              </div>

              <div className="brutal-panel p-6">
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed">
                  We synthesized insights from user interviews and usability testing through structured stakeholder workshops. By applying design thinking methodology, we transformed individual observations into strategic understanding of both our application's challenges and the broader ecosystem in which our users operate. This systematic approach enabled us to identify patterns and prioritize solutions that would deliver the greatest impact.
                </p>
              </div>
            </Card>
            
            {/* Mobile version without card */}
            <div className="md:hidden">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">What We Did</h2>
                <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto mb-8">
                  We developed a comprehensive research initiative that maximized impact while maintaining cost efficiency. Our strategic approach included:
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-4 mb-8">
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="/assets/advisor-research.png" 
                    alt="User Interviews Process"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">User Interviews</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Conducted extensive interviews to understand real user needs and pain points</p>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="/assets/ux-interviews-ra.png" 
                    alt="Usability Testing Sessions"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Usability Testing</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Gathered insights on how users actually interact with our product</p>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="/assets/Design-Workshop-JBP.png" 
                    alt="Stakeholder Workshop Sessions"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Stakeholder Workshops</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Hosted workshops to align on findings and define next steps</p>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden text-center">
                  <img 
                    src="/assets/ai-agent-ideation.png" 
                    alt="Design Thinking Methodology"
                    className="w-full aspect-[4/3] object-cover object-[center_25%]"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Design Thinking</h3>
                    <p className="text-[var(--brutal-ink)] text-sm">Applied structured methodology to organize and prioritize insights</p>
                  </div>
                </div>
              </div>

              <div className="brutal-panel p-4 md:p-6">
                <p className="text-[var(--brutal-muted)] text-base leading-relaxed">
                  We synthesized insights from user interviews and usability testing through structured stakeholder workshops. By applying design thinking methodology, we transformed individual observations into strategic understanding of both our application's challenges and the broader ecosystem in which our users operate. This systematic approach enabled us to identify patterns and prioritize solutions that would deliver the greatest impact.
                </p>
              </div>
            </div>
          </div>

          {/* Research Findings */}
          <div className="mb-12">
            {/* Subtle divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
            
            <Card className="brutal-panel p-4 md:p-8 mb-12 md:block hidden">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">Key Research Insights</h2>
                <p className="text-[var(--brutal-muted)] text-xl leading-relaxed max-w-4xl mx-auto">
                  Our usability testing across multiple teams revealed three critical themes that shaped our strategic roadmap.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Theme 1: Interface Complexity */}
                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay
                    src="/assets/promoai-storyboard.png" 
                    alt="Interface Overwhelm Research Findings"
                    className="w-full h-44 sm:h-48 md:h-52"
                  />
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4 text-center break-words">Interface Overwhelm</h3>
                    <p className="text-[var(--brutal-ink)] text-lg mb-6 text-center break-words">
                      Users consistently struggled with complex, cluttered interfaces that made simple tasks difficult.
                    </p>
                    <p className="text-[var(--brutal-ink)] text-sm leading-relaxed break-words">
                      Users consistently struggled with dense layouts, unclear navigation, and confusing terminology. Simple tasks required excessive scrolling and clicking, leading to frustration and reduced productivity across all user groups.
                    </p>
                  </div>
                </div>

                {/* Theme 2: Lack of Feedback */}
                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay 
                    src="/assets/user-test-findings-example.png" 
                    alt="Missing Guidance Research Findings"
                    className="w-full h-44 sm:h-48 md:h-52"
                  />
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4 text-center break-words">Missing Guidance</h3>
                    <p className="text-[var(--brutal-ink)] text-lg mb-6 text-center break-words">
                      Users lacked essential feedback and guidance systems to understand system state and available actions.
                    </p>
                    <p className="text-[var(--brutal-ink)] text-sm leading-relaxed break-words">
                      Users received no feedback after completing actions and lacked clear system status indicators. Missing tooltips and confusing error states led users to lose trust and frequently abandon tasks due to uncertainty.
                    </p>
                  </div>
                </div>

                {/* Theme 3: Visualization Needs */}
                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay 
                    src="/assets/data-viz-gap.png" 
                    alt="Data Visualization Gap Research Findings"
                    className="w-full h-44 sm:h-48 md:h-52"
                  />
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4 text-center break-words">Data Visualization Gap</h3>
                    <p className="text-[var(--brutal-ink)] text-lg mb-6 text-center break-words">
                      Users needed better ways to visualize and understand complex promotion data and performance metrics.
                    </p>
                    <p className="text-[var(--brutal-ink)] text-sm leading-relaxed break-words">
                      Users encountered sparse forecast pages with limited context and struggled to compare performance data. The lack of visual representations and chart options forced users to rely on external tools like Excel for analysis.
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Positive Findings */}
              <div className="mt-12 brutal-panel p-8">
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4 text-center">What Users Loved</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <CheckCircle weight="duotone" className="w-8 h-8 text-[var(--brutal-ink)] mx-auto mb-3" />
                    <h4 className="text-[var(--brutal-ink)] font-semibold mb-2">Campaign Creation</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Users found the campaign creation process straightforward and easy to navigate</p>
                  </div>
                  <div className="text-center">
                    <CheckCircle weight="duotone" className="w-8 h-8 text-[var(--brutal-ink)] mx-auto mb-3" />
                    <h4 className="text-[var(--brutal-ink)] font-semibold mb-2">Forecast Hierarchy</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Users appreciated being able to see hierarchy in forecast analysis</p>
                  </div>
                  <div className="text-center">
                    <CheckCircle weight="duotone" className="w-8 h-8 text-[var(--brutal-ink)] mx-auto mb-3" />
                    <h4 className="text-[var(--brutal-ink)] font-semibold mb-2">Promotion Cloning</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Users found cloning promotions easy and intuitive to accomplish</p>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Mobile version without card */}
            <div className="md:hidden">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-6">Key Research Insights</h2>
                <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
                  Our usability testing across multiple teams revealed three critical themes that shaped our strategic roadmap.
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-8">
                {/* Theme 1: Interface Complexity */}
                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay 
                          src="/assets/promoai-storyboard.png" 
                    alt="Interface Overwhelm Research Findings"
                    className="w-full h-44 sm:h-48 md:h-52"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-4 text-center">Interface Overwhelm</h3>
                    <p className="text-[var(--brutal-ink)] text-base mb-4 text-center">
                      Users consistently struggled with complex, cluttered interfaces that made simple tasks difficult.
                    </p>
                    <p className="text-[var(--brutal-ink)] text-sm leading-relaxed">
                      Users consistently struggled with dense layouts, unclear navigation, and confusing terminology. Simple tasks required excessive scrolling and clicking, leading to frustration and reduced productivity across all user groups.
                    </p>
                  </div>
                </div>

                {/* Theme 2: Lack of Feedback */}
                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay 
                    src="/assets/user-test-findings-example.png" 
                    alt="Missing Guidance Research Findings"
                    className="w-full h-44 sm:h-48 md:h-52"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-4 text-center">Missing Guidance</h3>
                    <p className="text-[var(--brutal-ink)] text-base mb-4 text-center">
                      Users lacked essential feedback and guidance systems to understand system state and available actions.
                    </p>
                    <p className="text-[var(--brutal-ink)] text-sm leading-relaxed">
                      Users received no feedback after completing actions and lacked clear system status indicators. Missing tooltips and confusing error states led users to lose trust and frequently abandon tasks due to uncertainty.
                    </p>
                  </div>
                </div>

                {/* Theme 3: Visualization Needs */}
                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay 
                    src="/assets/data-viz-gap.png" 
                    alt="Data Visualization Gap Research Findings"
                    className="w-full h-44 sm:h-48 md:h-52"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-4 text-center">Data Visualization Gap</h3>
                    <p className="text-[var(--brutal-ink)] text-base mb-4 text-center">
                      Users needed better ways to visualize and understand complex promotion data and performance metrics.
                    </p>
                    <p className="text-[var(--brutal-ink)] text-sm leading-relaxed">
                      Users encountered sparse forecast pages with limited context and struggled to compare performance data. The lack of visual representations and chart options forced users to rely on external tools like Excel for analysis.
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Positive Findings */}
              <div className="mt-8 brutal-panel p-6">
                <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-4 text-center">What Users Loved</h3>
                <div className="grid grid-cols-1 gap-6">
                  <div className="text-center">
                    <CheckCircle weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mx-auto mb-2" />
                    <h4 className="text-[var(--brutal-ink)] font-semibold mb-2">Campaign Creation</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Users found the campaign creation process straightforward and easy to navigate</p>
                  </div>
                  <div className="text-center">
                    <CheckCircle weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mx-auto mb-2" />
                    <h4 className="text-[var(--brutal-ink)] font-semibold mb-2">Forecast Hierarchy</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Users appreciated being able to see hierarchy in forecast analysis</p>
                  </div>
                  <div className="text-center">
                    <CheckCircle weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mx-auto mb-2" />
                    <h4 className="text-[var(--brutal-ink)] font-semibold mb-2">Promotion Cloning</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Users found cloning promotions easy and intuitive to accomplish</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analysis & Prioritization */}
          <div className="mb-12">
            {/* Subtle divider */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
            
            <Card className="brutal-panel p-4 md:p-8 mb-12 md:block hidden">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">Analysis & Prioritization</h2>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed max-w-4xl mx-auto">
                  We combined user-reported issues with our testing findings through affinity analysis to identify strategic initiatives for our roadmap. Following this synthesis, we conducted an impact/effort evaluation to prioritize the most valuable work that would deliver meaningful improvements to user experience.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="brutal-panel p-6">
                  <div className="flex items-center mb-4">
                    <Target weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                    <h3 className="text-xl font-semibold text-[var(--brutal-ink)]">Affinity Analysis</h3>
                  </div>
                  <p className="text-[var(--brutal-ink)] mb-4">
                    We organized all user feedback, usability issues, and research insights to identify patterns and larger themes that should drive our roadmap.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <CheckCircle weight="duotone" className="w-4 h-4 text-[var(--brutal-ink)]" />
                      <span className="text-[var(--brutal-muted)] text-sm">Grouped similar user pain points</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle weight="duotone" className="w-4 h-4 text-[var(--brutal-ink)]" />
                      <span className="text-[var(--brutal-muted)] text-sm">Identified recurring themes</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle weight="duotone" className="w-4 h-4 text-[var(--brutal-ink)]" />
                      <span className="text-[var(--brutal-muted)] text-sm">Connected insights to business impact</span>
                    </li>
                  </ul>
                </div>
                
                <div className="brutal-panel p-6">
                  <div className="flex items-center mb-4">
                    <TrendUp weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                    <h3 className="text-xl font-semibold text-[var(--brutal-ink)]">Impact/Effort Exercise</h3>
                  </div>
                  <p className="text-[var(--brutal-ink)] mb-4">
                    We evaluated each potential solution based on user impact and development effort to create a strategic roadmap that maximizes value.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-medium mb-2">High Impact, Low Effort (Quick Wins)</h4>
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Filtering</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Notifications</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">User onboarding & guidance</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-medium mb-2">High Impact, High Effort (Strategic)</h4>
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Auto-creation & automation</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Configuration UI overhaul</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Dashboards</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Mobile version without card */}
            <div className="md:hidden">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Analysis & Prioritization</h2>
                <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
                  We combined user-reported issues with our testing findings through affinity analysis to identify strategic initiatives for our roadmap. Following this synthesis, we conducted an impact/effort evaluation to prioritize the most valuable work that would deliver meaningful improvements to user experience.
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-8">
                <div className="brutal-panel p-6">
                  <div className="flex items-center mb-4">
                    <Target weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                    <h3 className="text-xl font-semibold text-[var(--brutal-ink)]">Affinity Analysis</h3>
                  </div>
                  <p className="text-[var(--brutal-ink)] mb-4">
                    We organized all user feedback, usability issues, and research insights to identify patterns and larger themes that should drive our roadmap.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <CheckCircle weight="duotone" className="w-4 h-4 text-[var(--brutal-ink)]" />
                      <span className="text-[var(--brutal-muted)] text-sm">Grouped similar user pain points</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle weight="duotone" className="w-4 h-4 text-[var(--brutal-ink)]" />
                      <span className="text-[var(--brutal-muted)] text-sm">Identified recurring themes</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle weight="duotone" className="w-4 h-4 text-[var(--brutal-ink)]" />
                      <span className="text-[var(--brutal-muted)] text-sm">Connected insights to business impact</span>
                    </li>
                  </ul>
                </div>
                
                <div className="brutal-panel p-6">
                  <div className="flex items-center mb-4">
                    <TrendUp weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                    <h3 className="text-xl font-semibold text-[var(--brutal-ink)]">Impact/Effort Exercise</h3>
                  </div>
                  <p className="text-[var(--brutal-ink)] mb-4">
                    We evaluated each potential solution based on user impact and development effort to create a strategic roadmap that maximizes value.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-medium mb-2">High Impact, Low Effort (Quick Wins)</h4>
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Filtering</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Notifications</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">User onboarding & guidance</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-medium mb-2">High Impact, High Effort (Strategic)</h4>
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Auto-creation & automation</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Configuration UI overhaul</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full" aria-hidden="true"></div>
                          <span className="text-[var(--brutal-muted)] text-sm">Dashboards</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User Suggestions & Roadmap */}
          <Card className="brutal-panel p-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">User-Driven Suggestions & Strategic Roadmap</h2>
              <p className="text-[var(--brutal-muted)] text-lg leading-relaxed max-w-4xl mx-auto">
                Based on our research, users provided specific suggestions that directly informed our strategic roadmap priorities.
              </p>
            </div>
            
            {/* User Suggestions */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6 text-center">What Users Asked For</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="brutal-panel p-6">
                  <div className="flex items-center mb-4">
                    <Lightning weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">AI Integration</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-[var(--brutal-ink)]">• Bot for natural language input to create promotions</p>
                    <p className="text-[var(--brutal-ink)]">• AI recommendations for event invitations</p>
                    <p className="text-[var(--brutal-ink)]">• Scenarios to be saved automatically</p>
                  </div>
                </div>
                
                <div className="brutal-panel p-6">
                  <div className="flex items-center mb-4">
                    <TrendUp weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">Graphics & Visualization</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-[var(--brutal-ink)]">• Creating visually appealing graphics for decision-making guidance</p>
                    <p className="text-[var(--brutal-ink)]">• Visual representation of forecasts and historical data</p>
                    <p className="text-[var(--brutal-ink)]">• Stacking metrics for better comparison</p>
                  </div>
                </div>
                
                <div className="brutal-panel p-6">
                  <div className="flex items-center mb-4">
                    <Target className="w-6 h-6 text-[var(--brutal-ink)] mr-3" />
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">Dashboards & UX</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-[var(--brutal-ink)]">• Dashboard-like summary and keyboard navigation</p>
                    <p className="text-[var(--brutal-ink)]">• Quick filters for promotion attributes</p>
                    <p className="text-[var(--brutal-ink)]">• Grouping metrics, utilizing screen, tooltips or help section</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Strategic Roadmap */}
            <div>
              <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6 text-center">Our Strategic Response</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay
                    src="/assets/ai-agent-concept.png"
                    alt="Priority 1: Auto-Creation & AI Integration"
                    className="w-full h-48 sm:h-56 md:h-64"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <span className="brutal-chip bg-[var(--brutal-mustard)] text-[var(--brutal-ink)] text-sm font-bold px-3 py-1 mr-4">Priority 1</span>
                      <h4 className="text-xl font-semibold text-[var(--brutal-ink)]">Auto-Creation & AI Integration</h4>
                    </div>
                    <p className="text-[var(--brutal-muted)] mb-4">Directly addressing user requests for natural language promotion creation and automated scenario saving - the highest impact solution that eliminates manual work.</p>
                    <div className="space-y-2 text-sm text-[var(--brutal-ink)]">
                      <p>• Bot for natural language input</p>
                      <p>• AI recommendations</p>
                      <p>• Automatic scenario saving</p>
                    </div>
                  </div>
                </div>

                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay
                    src="/assets/cognira-website-homepage.png"
                    alt="Priority 2: Configuration UI & Visual Improvements"
                    className="w-full h-48 sm:h-56 md:h-64"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <span className="brutal-chip bg-[var(--brutal-mustard)] text-[var(--brutal-ink)] text-sm font-bold px-3 py-1 mr-4">Priority 2</span>
                      <h4 className="text-xl font-semibold text-[var(--brutal-ink)]">Configuration UI & Visual Improvements</h4>
                    </div>
                    <p className="text-[var(--brutal-muted)] mb-4">Redesigning interfaces to address the overwhelming complexity users reported, with enhanced graphics and visual representations they specifically requested.</p>
                    <div className="space-y-2 text-sm text-[var(--brutal-ink)]">
                      <p>• Simplified configuration flows</p>
                      <p>• Visual forecast representations</p>
                      <p>• Better graphics for decision-making</p>
                    </div>
                  </div>
                </div>

                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay
                    src="/assets/enhanced-filtering.png"
                    alt="Priority 3: Enhanced Filtering & Navigation"
                    className="w-full h-48 sm:h-56 md:h-64"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <span className="brutal-chip bg-[var(--brutal-kraft)] text-[var(--brutal-ink)] text-sm font-bold px-3 py-1 mr-4">Priority 3</span>
                      <h4 className="text-xl font-semibold text-[var(--brutal-ink)]">Enhanced Filtering & Navigation</h4>
                    </div>
                    <p className="text-[var(--brutal-muted)] mb-4">Quick filters for promotion attributes and improved navigation with tooltips and indicators as users specifically requested.</p>
                    <div className="space-y-2 text-sm text-[var(--brutal-ink)]">
                      <p>• Quick filters for promotion attributes</p>
                      <p>• Tooltips and status indicators</p>
                      <p>• Clearer navigation paths</p>
                    </div>
                  </div>
                </div>

                <div className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay
                    src="/assets/workflow-flexibility.png"
                    alt="Priority 4: Workflow Flexibility"
                    className="w-full h-48 sm:h-56 md:h-64"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <span className="brutal-chip bg-[var(--brutal-kraft)] text-[var(--brutal-ink)] text-sm font-bold px-3 py-1 mr-4">Priority 4</span>
                      <h4 className="text-xl font-semibold text-[var(--brutal-ink)]">Workflow Flexibility</h4>
                    </div>
                    <p className="text-[var(--brutal-muted)] mb-4">Flexible workflows to adapt to different client processes and changing situations, such as last minute updates.</p>
                    <div className="space-y-2 text-sm text-[var(--brutal-ink)]">
                      <p>• Adapt to client-specific processes</p>
                      <p>• Support last-minute updates</p>
                      <p>• Reduce rigid step lock-in</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* The Impact */}
          <Card className="brutal-panel p-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">The Impact</h2>
              <p className="text-[var(--brutal-muted)] text-lg leading-relaxed max-w-4xl mx-auto">
                This research-driven approach transformed how we build products and deliver value to our users.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Research-Driven Culture</h4>
                    <p className="text-[var(--brutal-ink)]">Established systematic research processes that now inform all product decisions across the organization.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Strategic Roadmap</h4>
                    <p className="text-[var(--brutal-ink)]">Created a comprehensive roadmap based on real user needs rather than assumptions, ensuring development efforts align with user value.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Improved User Experience</h4>
                    <p className="text-[var(--brutal-ink)]">Delivered solutions that directly address user pain points, significantly improving satisfaction and task completion rates.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Cross-Functional Alignment</h4>
                    <p className="text-[var(--brutal-ink)]">Built strong collaboration between UX, Product, and Engineering teams around shared user insights and priorities.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">AI-Powered Solutions</h4>
                    <p className="text-[var(--brutal-ink)]">Successfully integrated AI capabilities that users actually wanted, based on research insights rather than technology trends.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-[var(--brutal-ink)] mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-2">Scalable Process</h4>
                    <p className="text-[var(--brutal-ink)]">Created repeatable research and prioritization methods that continue to guide product development.</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Leadership Reflection */}
          <Card className="brutal-panel p-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">What I Learned</h2>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="brutal-panel p-8 mb-8">
                <Quotes weight="duotone" className="w-8 h-8 text-[var(--brutal-ink)] mb-4" />
                <blockquote className="text-[var(--brutal-ink)] text-xl italic leading-relaxed mb-4">
                  "The key was showing that research doesn't slow you down—it speeds you up by ensuring you're building the right things. When you understand your users deeply, every decision becomes clearer."
                </blockquote>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="brutal-panel p-6">
                  <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-3">Start Small, Think Big</h4>
                  <p className="text-[var(--brutal-ink)]">Low-cost research methods can yield high-impact insights. You don't need a huge budget to understand your users deeply.</p>
                </div>
                
                <div className="brutal-panel p-6">
                  <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-3">Structure Drives Results</h4>
                  <p className="text-[var(--brutal-ink)]">Using design thinking methodology helped us organize insights and make strategic decisions rather than getting lost in data.</p>
                </div>
                
                <div className="brutal-panel p-6">
                  <h4 className="text-lg font-semibold text-[var(--brutal-ink)] mb-3">Prioritization is Everything</h4>
                  <p className="text-[var(--brutal-ink)]">Impact/effort analysis transformed overwhelming insights into a clear, actionable roadmap that the whole team could rally behind.</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Call-to-Action */}
          <Card className="brutal-panel p-8 text-center">
            <h3 className="text-3xl font-bold text-[var(--brutal-ink)] mb-4">Ready to build user-centered products?</h3>
            <p className="text-[var(--brutal-muted)] mb-8 max-w-3xl mx-auto text-lg">
              I help organizations establish research-driven cultures that transform how they build products and deliver value to users.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                variant="outline" 
                className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)]"
              >
                <Link to="/ProfessionalProjects">View More Projects</Link>
              </Button>
              <Button 
                asChild 
                className="brutal-btn  text-[var(--brutal-ink)] border-0"
              >
                <Link to="/Contact">Let's Connect</Link>
              </Button>
            </div>
          </Card>
        </div>
      </main>
    </div>
  )
}